import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { PrismTable } from '@mylighthouse/prism-ember';
import type { IconName } from '@mylighthouse/prism-ember/types/components/prism/icon';
import type {
  CustomEmberTableColumn,
  CustomEmberTableRow,
} from '@mylighthouse/prism-ember/types/components/prism/table';
import dayjs from 'dayjs';
import { eq, or } from 'ember-truth-helpers';
import type { Hotelinfo } from 'frontend-utils/models';
import GeneralTablesCellsTdWithCustomClass from 'lumen/components/general/tables/cells/td-with-custom-class';
import GeneralTablesCellsThIconWithTooltip from 'lumen/components/general/tables/cells/th-icon-with-tooltip';
import HotelPricingAssistantDayDetailTabsRecommendationReasonAccordion from 'lumen/components/hotel/pricing-assistant/day-detail/tabs/recommendation-reason-accordion';
import type { Carecommendation } from 'lumen/models';
import type {
  CompsetRecommendationReason,
  CompsetRecommendationReasonDetailFallbackReason,
  CompsetRecommendationReasonDetailFullReason,
  CompsetRecommendationReasonDetailVsReasons,
} from 'lumen/models/pa-recommendation-reason';
import {
  RECOMMENDATION_REASON_TRANSLATION_KEY,
  RECOMMENDATION_REASON_TYPE_COMPSET,
} from 'lumen/utils/constants/pricing-assistant-constants';

const COLUMN_VALUE_PATHS = {
  COMPETITOR_NAME: 'competitor_name',
  CURRENT_PRICE: 'current_price',
  DIFFERENCE: 'difference',
} as const;

type CompsetRecommendationReasonTransformed = {
  icon: IconName;
  isIncrease: boolean | null;
  label: string;
  translationKey: string;
  values: Record<string, unknown> | null;
} & (
  | {
      details: CompsetRecommendationReasonDetailFallbackReason[];
      reasonType: typeof RECOMMENDATION_REASON_TYPE_COMPSET.FALLBACK;
    }
  | {
      details: CompsetRecommendationReasonDetailFullReason[];
      reasonType: typeof RECOMMENDATION_REASON_TYPE_COMPSET.SOLDOUT;
    }
  | {
      details: CompsetRecommendationReasonDetailVsReasons;
      reasonType:
        | typeof RECOMMENDATION_REASON_TYPE_COMPSET.VS_NORMAL
        | typeof RECOMMENDATION_REASON_TYPE_COMPSET.VS_YESTERDAY;
    }
);

interface HotelPricingAssistantDayDetailTabsCompsetRecommendationReasonsSignature {
  Args: {
    carecommendationOfSelectedDay?: Carecommendation;
    date: string;
  };
}

export default class HotelPricingAssistantDayDetailTabsCompsetRecommendationReasons extends Component<HotelPricingAssistantDayDetailTabsCompsetRecommendationReasonsSignature> {
  @service('hotel-and-market')
  declare hotelAndMarketService: Services['hotel-and-market'];
  @service declare intl: Services['intl'];
  @service declare store: Services['store'];

  get formattedWeekday(): string {
    return dayjs(this.args.date).format('dddd');
  }

  get reasons(): CompsetRecommendationReasonTransformed[] {
    const { carecommendationOfSelectedDay } = this.args;

    const reasons =
      carecommendationOfSelectedDay?.paRecommendationReason?.compsetReasons;

    if (!reasons) {
      return [];
    }

    return reasons.reduce((accumulator, reason) => {
      /*
        Only show reasons for which we have a translation on frontend.
        Currently, recommendations without reasons return a fallback
        translation key, but they are not defined in FE yet.
      */
      if (RECOMMENDATION_REASON_TRANSLATION_KEY[reason.translation_key]) {
        const details = this.getDetails(reason);
        const label = this.getLabel(reason);

        accumulator.push({
          details,
          icon: 'business-type-market',
          isIncrease: reason.is_increase,
          label,
          reasonType: reason.reason_type,
          translationKey: reason.translation_key,
          values: reason.values,
        } as CompsetRecommendationReasonTransformed);
      }

      return accumulator;
    }, [] as CompsetRecommendationReasonTransformed[]);
  }

  /*
    Sorting is done similarly to that for competitor list in the tooltip,
    i.e. by descending order of rate, then by hotel name
  */
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  private getDetails(reason: CompsetRecommendationReason) {
    switch (reason.reason_type) {
      case RECOMMENDATION_REASON_TYPE_COMPSET.FALLBACK: {
        return reason.details.map((detail) => {
          const translationKey =
            RECOMMENDATION_REASON_TRANSLATION_KEY[detail.translation_key];

          return {
            ...detail,
            formattedDetail: translationKey ? this.intl.t(translationKey) : '',
          };
        }) as CompsetRecommendationReasonDetailFallbackReason[];
      }

      case RECOMMENDATION_REASON_TYPE_COMPSET.SOLDOUT: {
        const details = reason.details.map((detail) => {
          const hotelinfoObject = this.store.peekRecord<Hotelinfo>(
            'hotelinfo',
            detail.hotelinfo,
          );

          return {
            ...detail,
            // @ts-expect-error: 'hotelinfoObject' is possibly 'null'.
            name: hotelinfoObject.name,
          };
        }) as CompsetRecommendationReasonDetailFullReason[];

        return details.sort((a, b) => {
          return a.name.localeCompare(b.name);
        });
      }

      case RECOMMENDATION_REASON_TYPE_COMPSET.VS_NORMAL:
      case RECOMMENDATION_REASON_TYPE_COMPSET.VS_YESTERDAY: {
        const isVsNormalReasonType =
          reason.reason_type === RECOMMENDATION_REASON_TYPE_COMPSET.VS_NORMAL;

        const rows: CustomEmberTableRow[] = [];
        const columns: CustomEmberTableColumn[] = [
          {
            name: this.intl.t('general.competitors'),
            valuePath: COLUMN_VALUE_PATHS.COMPETITOR_NAME,
            textAlign: 'left',
            tdComponent: GeneralTablesCellsTdWithCustomClass,
          },
          {
            name: this.intl.t('pricing_assistant.current_price'),
            valuePath: COLUMN_VALUE_PATHS.CURRENT_PRICE,
            textAlign: 'right',
            width: isVsNormalReasonType ? 120 : 100, // Enough space for column label and info icon in case of VS_NORMAL reason type
          },
          {
            name: '',
            valuePath: COLUMN_VALUE_PATHS.DIFFERENCE,
            textAlign: 'left',
            tdComponent: GeneralTablesCellsTdWithCustomClass,
            // Ideally this column should have an explicit min and max width to ensure it takes up space based on the content.
            // However, ember table seems to not respect these values: https://github.com/Addepar/ember-table/issues/817
            // Therefore, using a fixed width for now.
            width: 70,
          },
        ];

        // In case of VS_NORMAL, add tooltip to the current price column
        if (isVsNormalReasonType) {
          const currentPriceColumnConfig = columns.find(
            (column) => column.valuePath === COLUMN_VALUE_PATHS.CURRENT_PRICE,
          );

          if (currentPriceColumnConfig) {
            currentPriceColumnConfig.thComponent =
              GeneralTablesCellsThIconWithTooltip;
            currentPriceColumnConfig['tooltip'] = {
              content: this.intl.t(
                'pricing_assistant.reason.compset.vs_normal.table.tooltip',
                {
                  weekday: this.formattedWeekday,
                },
              ),
            };
          }
        }

        const details = reason.details.map((detail) => {
          const hotelinfoObject = this.store.peekRecord<Hotelinfo>(
            'hotelinfo',
            detail.hotelinfo,
          );

          const formattedRate = this.hotelAndMarketService.formatRate(
            detail.current_rate,
            { longFormat: false },
          );

          const formattedRateNumber = parseInt(
            (
              this.hotelAndMarketService.formatRate(detail.current_rate, {
                includeCurrencySymbol: false,
                format: {
                  maximumFractionDigits: 0,
                  minimumFractionDigits: 0,
                  useGrouping: false,
                },
              }) ?? '0'
            ).toString(),
            10,
          );

          return {
            ...detail,
            // @ts-expect-error: 'hotelinfoObject' is possibly 'null'.
            name: hotelinfoObject.name,
            formattedRate,
            formattedRateNumber,
          };
        }) as CompsetRecommendationReasonDetailFullReason[];

        details.sort((a, b) => {
          // sort by formatted current rate
          const rateDifference = b.formattedRateNumber - a.formattedRateNumber;

          if (rateDifference !== 0) {
            return rateDifference;
          }

          // sort by hotel name in case formatted current rates are same
          return a.name.localeCompare(b.name);
        });

        // Each detail object represents a competitor
        details.forEach((detail) => {
          let difference;
          let formattedDifference;

          if (
            detail.reason_type ===
            RECOMMENDATION_REASON_TYPE_COMPSET.VS_YESTERDAY
          ) {
            difference = detail.price_difference;
            formattedDifference = this.hotelAndMarketService.formatRate(
              difference,
              { includeSign: true },
            ); // add sign
          } else if (
            detail.reason_type === RECOMMENDATION_REASON_TYPE_COMPSET.VS_NORMAL
          ) {
            difference = detail.price_variance;
            formattedDifference = this.intl.formatNumber(difference, {
              style: 'percent',
              signDisplay: 'always',
            });
          }

          rows.push({
            [COLUMN_VALUE_PATHS.COMPETITOR_NAME]: {
              formattedValue: detail.name,
              customClass: 'line-clamp-2',
            },
            [COLUMN_VALUE_PATHS.CURRENT_PRICE]: detail.formattedRate,
            [COLUMN_VALUE_PATHS.DIFFERENCE]: {
              formattedValue: formattedDifference,
              customClass:
                (difference ?? 0) > 0 ? 'text-success' : 'text-critical',
            },
          });
        });

        return { columns, rows };
      }

      default: {
        return [];
      }
    }
  }

  private getLabel(reason: CompsetRecommendationReason): string {
    const { reason_type, translation_key, values } = reason;

    const translationKey =
      RECOMMENDATION_REASON_TRANSLATION_KEY[translation_key];

    if (!translationKey) {
      return '';
    }

    switch (reason_type) {
      case RECOMMENDATION_REASON_TYPE_COMPSET.FALLBACK: {
        return this.intl.t(translationKey);
      }

      case RECOMMENDATION_REASON_TYPE_COMPSET.SOLDOUT:
      case RECOMMENDATION_REASON_TYPE_COMPSET.VS_YESTERDAY: {
        return this.intl.t(translationKey, {
          // @ts-expect-error: amount is unknown
          amount: values?.['amount'],
        });
      }

      case RECOMMENDATION_REASON_TYPE_COMPSET.VS_NORMAL: {
        return this.intl.t(translationKey, {
          // @ts-expect-error: amount is unknown
          amount: values?.['amount'],
          weekday: this.formattedWeekday,
        });
      }

      default: {
        return '';
      }
    }
  }

  <template>
    {{#each this.reasons as |reason|}}
      <HotelPricingAssistantDayDetailTabsRecommendationReasonAccordion
        @reason={{reason}}
      >
        {{#if
          (eq reason.reasonType RECOMMENDATION_REASON_TYPE_COMPSET.FALLBACK)
        }}
          {{#each reason.details as |detail|}}
            <p class="text-200--regular">{{detail.formattedDetail}}</p>
          {{/each}}
        {{else if
          (eq reason.reasonType RECOMMENDATION_REASON_TYPE_COMPSET.SOLDOUT)
        }}
          <div class="flex flex-col gap-200">
            {{#each reason.details as |detail|}}
              <p class="text-200--medium">{{detail.name}}</p>
            {{/each}}
          </div>
        {{else if
          (or
            (eq reason.reasonType RECOMMENDATION_REASON_TYPE_COMPSET.VS_NORMAL)
            (eq
              reason.reasonType RECOMMENDATION_REASON_TYPE_COMPSET.VS_YESTERDAY
            )
          )
        }}
          <PrismTable
            @columns={{reason.details.columns}}
            @fillMode="first-column"
            @rows={{reason.details.rows}}
            @widthConstraint="eq-container"
          />
        {{/if}}
      </HotelPricingAssistantDayDetailTabsRecommendationReasonAccordion>
    {{/each}}
  </template>
}
